import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Send, Mail, MessageCircle, MessageSquare, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CHIPS = ["{name}", "{prize}", "{coupon}", "{store}"];

export default function RetargetingPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(
    "Hi {name}, you won {prize} at {store}! Use code {coupon} to claim."
  );
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [msgCfg, setMsgCfg] = useState(null);
  // ── Real email preview (structured template) ───────────────────
  // Populated by /messages/preview-email whenever the vendor changes
  // campaign / subject / body. Kept identical to the actual send
  // pipeline by rendering server-side via send_retargeting_email.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewDebounceRef = useRef(null);

  useEffect(() => {
    api.listCampaigns().then((r) => {
      // Sort newest-first so freshly-created duplicates show at top —
      // matches vendor mental model when they've just made a fix on a
      // failed campaign and are re-targeting from the new copy.
      const sorted = [...(r.data || [])].sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setCampaigns(sorted);
      if (sorted.length) setCampaignId(sorted[0].id);
    });
  }, []);
  useEffect(() => {
    api.listMessages().then((r) => setHistory(r.data || []));
  }, []);
  useEffect(() => {
    api
      .messagesConfig()
      .then((r) => setMsgCfg(r.data))
      .catch(() => setMsgCfg(null));
  }, []);

  const whatsappLocked = !msgCfg?.channels?.whatsapp?.enabled;
  const smsLocked = !msgCfg?.channels?.sms?.enabled;
  // If the current channel is locked (WhatsApp or SMS), fall back to email
  // so the Send button doesn't hit a 403.
  useEffect(() => {
    if (channel === "whatsapp" && whatsappLocked) setChannel("email");
    if (channel === "sms" && smsLocked) setChannel("email");
  }, [channel, whatsappLocked, smsLocked]);

  const insert = (chip) => setBody((b) => `${b}${b.endsWith(" ") || !b ? "" : " "}${chip}`);

  // ── Campaign dropdown option builder ─────────────────────────────
  // The <SelectItem value> is ALWAYS campaign.id (unique) so React +
  // Radix can never collapse duplicates. The LABEL is either the raw
  // name or "Name (Status — 12 Jul)" when the name collides with
  // another campaign — this is the actual bug this page had, where a
  // vendor with two "Festive Spin" campaigns saw identical rows and
  // couldn't tell them apart. We compute the collision map up front
  // instead of per-render inside the map body.
  const campaignOptions = useMemo(() => {
    const nameCount = new Map();
    for (const c of campaigns) {
      const key = (c?.name || "").trim().toLowerCase();
      nameCount.set(key, (nameCount.get(key) || 0) + 1);
    }
    const fmtStatus = (s) => {
      const raw = (s || "active").toString().replace(/_/g, " ");
      // Title-case each word so "pending payment" reads "Pending Payment".
      return raw.replace(/\b\w/g, (m) => m.toUpperCase());
    };
    const fmtDate = (iso) => {
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
        });
      } catch {
        return "";
      }
    };
    return campaigns.map((c) => {
      const nameKey = (c?.name || "").trim().toLowerCase();
      const collides = (nameCount.get(nameKey) || 0) > 1;
      const badge = collides
        ? ` (${fmtStatus(c.status)} — ${fmtDate(c.created_at)})`
        : "";
      return {
        id: c.id,
        label: `${c.name || "Untitled campaign"}${badge}`,
      };
    });
  }, [campaigns]);

  const sample = useMemo(() => {
    return body
      .replace("{name}", "Riya")
      .replace("{prize}", "50% Off Pizza")
      .replace("{coupon}", "CTD-XMAS-9JTMMF")
      .replace("{store}", campaigns.find((c) => c.id === campaignId)?.name || "our store");
  }, [body, campaigns, campaignId]);

  // ── Debounced structured-email preview fetch ──────────────────────
  // Skip when the vendor is composing for a non-email channel — the
  // template is email-only. Also skip if no campaign is selected.
  useEffect(() => {
    if (channel !== "email" || !campaignId) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const r = await api.previewRetargetingEmail({
          campaign_id: campaignId,
          body,
          subject: subject || undefined,
        });
        setPreview(r.data);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "object" ? detail?.message : detail;
        setPreviewError(msg || "Couldn't render preview");
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [channel, campaignId, subject, body]);

  const refreshPreview = async () => {
    if (channel !== "email" || !campaignId) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const r = await api.previewRetargetingEmail({
        campaign_id: campaignId,
        body,
        subject: subject || undefined,
      });
      setPreview(r.data);
      toast.success("Preview refreshed");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      setPreviewError(msg || "Couldn't render preview");
      // Manual refresh only — the debounced auto-preview above sticks
      // with the inline `previewError` to avoid toast-spam on every
      // keystroke pause. This click is user-initiated so a toast is fine.
      toast.error(msg || "Couldn't render preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const send = async () => {
    if (!campaignId) return toast.error("Pick a campaign");
    setSending(true);
    try {
      const r = await api.sendMessages({
        campaign_id: campaignId,
        channel,
        body,
        // Subject only meaningful for email; other channels ignore it
        // server-side. Send undefined when blank so backend falls back
        // to the retargeting-email default subject line.
        subject: channel === "email" && subject ? subject : undefined,
      });
      const sent = r.data?.sent || 0;
      const skipped = Array.isArray(r.data?.skipped) ? r.data.skipped : [];
      if (sent > 0) toast.success(`Sent to ${sent} lead(s)`);
      if (skipped.length > 0) {
        // Toast survives long enough for the vendor to click into wallet.
        toast.warning(
          `${skipped.length} message(s) skipped — low wallet balance. Top up to keep sending.`,
          {
            action: {
              label: "Top up",
              onClick: () => (window.location.href = "/vendor/wallet"),
            },
            duration: 8000,
          }
        );
      }
      if (sent === 0 && skipped.length === 0) toast.info("No messages sent.");
      const h = await api.listMessages();
      setHistory(h.data || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast.error(msg || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Retargeting</h1>
        <p className="text-muted-foreground text-sm">Re-engage your leads with personalized messages.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="p-5 lg:col-span-7 shadow-[var(--shadow-card)]">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Campaign</label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger data-testid="retargeting-campaign-select">
                  <SelectValue placeholder="Pick campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaignOptions.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      data-testid={`retargeting-campaign-option-${c.id}`}
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Tabs value={channel} onValueChange={setChannel} data-testid="retargeting-channel-tabs">
              <TabsList className="grid grid-cols-3 w-full md:w-fit">
                <TabsTrigger value="email" data-testid="channel-email">
                  <Mail className="h-4 w-4 mr-1" /> Email
                </TabsTrigger>
                <TabsTrigger
                  value="whatsapp"
                  data-testid="channel-whatsapp"
                  disabled={whatsappLocked}
                  className={whatsappLocked ? "opacity-60 relative" : ""}
                >
                  <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                  {whatsappLocked && (
                    <span className="ml-1.5 text-[9px] px-1 py-0 rounded bg-black text-[var(--graffiti-yellow)] uppercase tracking-wider font-bold">
                      Soon
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="sms"
                  data-testid="channel-sms"
                  disabled={smsLocked}
                  className={smsLocked ? "opacity-60 relative" : ""}
                >
                  <MessageSquare className="h-4 w-4 mr-1" /> SMS
                  {smsLocked && (
                    <span className="ml-1.5 text-[9px] px-1 py-0 rounded bg-black text-[var(--graffiti-yellow)] uppercase tracking-wider font-bold">
                      Soon
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value={channel} className="mt-4 space-y-3">
                {channel === "email" && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Subject line</label>
                    <Input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={`Reminder: your prize at ${
                        campaigns.find((c) => c.id === campaignId)?.name || "the store"
                      } is waiting`}
                      maxLength={120}
                      data-testid="retargeting-subject-input"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Leave blank to use the default subject line.
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      onClick={() => insert(c)}
                      className="text-xs px-2.5 py-1 rounded-full bg-accent text-accent-foreground hover:opacity-80"
                      data-testid="retargeting-variable-chip"
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <Textarea
                  rows={6}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Hi {name}, you won {prize} at {store}!"
                  data-testid="retargeting-message-textarea"
                />
                {channel === "email" && (
                  <p className="text-[11px] text-muted-foreground">
                    This text appears as a personalised note inside the
                    structured template — we automatically wrap it with the
                    prize, coupon code, expiry and a Claim Now button that
                    points to the winner&apos;s post-spin redirect link.
                  </p>
                )}
                {channel !== "email" && (
                  <p className="text-xs text-muted-foreground">
                    SMS sending is queued (real provider integration coming soon).
                  </p>
                )}
                {msgCfg?.email_cap_per_campaign && channel === "email" && (
                  <p className="text-xs text-muted-foreground" data-testid="retargeting-email-cap-hint">
                    Emails are capped at {msgCfg.email_cap_per_campaign} per campaign.
                  </p>
                )}
                <Button onClick={send} disabled={sending} data-testid="retargeting-send-button">
                  <Send className="h-4 w-4 mr-1" /> Send to all leads
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        </Card>

        <div className="lg:col-span-5">
          <Card className="p-5 shadow-[var(--shadow-card)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-base">Preview</h3>
              {channel === "email" && campaignId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshPreview}
                  disabled={previewLoading}
                  data-testid="retargeting-preview-refresh"
                  className="h-7 px-2 text-xs"
                >
                  {previewLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-1.5">Refresh</span>
                </Button>
              )}
            </div>

            {channel === "email" ? (
              /* Structured retargeting email preview — rendered by the
                 backend using the same code path as the actual send,
                 so what the vendor sees is EXACTLY what lands in the
                 recipient's inbox (subject line included). */
              <div className="space-y-3" data-testid="retargeting-email-preview">
                {!campaignId ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    Pick a campaign to see the preview.
                  </div>
                ) : previewError ? (
                  <div
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"
                    data-testid="retargeting-preview-error"
                  >
                    {previewError}
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs space-y-1">
                      <div>
                        <span className="text-muted-foreground">From:</span>{" "}
                        <span className="font-medium">CityDooh &lt;no-reply@citydooh.com&gt;</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">To:</span>{" "}
                        <span className="font-medium">
                          {preview?.sample?.recipient_display || "the winning lead"}
                        </span>
                        {preview?.sample?.used_real_lead === false && (
                          <Badge variant="outline" className="ml-2 text-[10px] py-0 h-4">
                            Sample data
                          </Badge>
                        )}
                      </div>
                      <div className="truncate">
                        <span className="text-muted-foreground">Subject:</span>{" "}
                        <span className="font-semibold">
                          {preview?.subject || "…"}
                        </span>
                      </div>
                    </div>

                    <div className="relative rounded-lg overflow-hidden border border-border bg-white">
                      {previewLoading && !preview && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-10">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      {/* Render the actual email HTML in a sandboxed iframe so
                          any wrapper CSS from the app can't leak into the
                          template — and vice versa. srcDoc lets us keep the
                          preview fully client-side after the initial fetch. */}
                      <iframe
                        title="Retargeting email preview"
                        srcDoc={preview?.html || "<p style='padding:24px;font-family:sans-serif;color:#6b7280;'>Loading preview…</p>"}
                        sandbox=""
                        className="w-full block bg-white"
                        style={{ height: 620, border: 0 }}
                        data-testid="retargeting-preview-iframe"
                      />
                    </div>

                    {preview?.sample?.claim_url && (
                      <p className="text-[11px] text-muted-foreground break-all">
                        <span className="font-medium">Claim Now link →</span>{" "}
                        {preview.sample.claim_url}
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              /* Non-email channels keep the phone-bubble preview — those
                 sends stay as plain body-only messages (no template). */
              <div
                className="mx-auto w-full max-w-[300px] rounded-[28px] border bg-black p-3 shadow-[var(--shadow-popover)]"
                data-testid="phone-preview"
              >
                <div className="rounded-[22px] bg-white overflow-hidden">
                  <div className="px-4 py-3 border-b border-zinc-200 text-xs text-zinc-500">
                    {channel === "whatsapp" ? "WhatsApp" : "SMS"}
                  </div>
                  <div className="p-3 min-h-[160px]">
                    <div className="max-w-[85%] rounded-2xl bg-zinc-100 px-3 py-2 text-sm text-zinc-900 whitespace-pre-wrap">
                      {sample}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Card className="p-5 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base">Send history</h3>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No messages sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="retargeting-send-history">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Channel</th>
                  <th className="py-2 pr-3">To</th>
                  <th className="py-2 pr-3">Body</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Sent at</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 40).map((m) => (
                  <tr key={m.id} className="border-b last:border-0 border-border">
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="capitalize">{m.channel}</Badge>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{m.to || "—"}</td>
                    <td className="py-2 pr-3 truncate max-w-xs">{m.body}</td>
                    <td className="py-2 pr-3 capitalize">{m.status}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
